import { usePage } from "@/components/page/PageContext";
import { PlayListDisplay } from "@/components/playlist/PlayListDisplay";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  bagBreakdownCellFormatter,
  bagBreakdownTooltipFormatter,
} from "@/lib/tooltipFormatters";
import {
  Card,
  ExploreDisplay,
  MultiSeriesChart,
  SingleSeriesChart,
} from "@/pages/data/DataChart";
import { useBaggageCarousels } from "@/queries/baggage";
import { getPlays } from "@/queries/plays";
import { getAggregateData, ZoneQuery } from "@/queries/report";
import { useReportContext } from "@/stores/ReportContext";
import { useSessionData } from "@/stores/SessionDataContext";
import { AlertCircle } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";

export function TrafficChart({
  query,
  title,
}: {
  query: ZoneQuery;
  title: string;
}) {
  const { reportId } = useReportContext();

  const { data: trafficData, isSuccess: trafficSuccess } = getAggregateData(
    [
      {
        query,
      },
    ],
    reportId,
    true
  );

  if (!trafficSuccess) {
    return null;
  }

  return (
    <Card title={title}>
      <SingleSeriesChart
        type={"column"}
        values={trafficData[0].values}
        labels={trafficData[0].labels}
        xAxisTitle={"Time"}
        yAxisTitle={"Passengers"}
      />
    </Card>
  );
}

function MultiCustomsPassengers() {
  const { reportId } = useReportContext();
  const { airport } = useSessionData();
  let customNames = [];
  const { data, isSuccess } = getAggregateData(
    airport.customs.map((t) => {
      customNames.push(t.name);
      return {
        query: {
          measure: "pax",
          customs_id: t.id,
          type: "ingress_customs",
        },
      };
    }),
    reportId
  );

  if (!isSuccess || data.length === 0) {
    return null;
  }
  return (
    <Card title="Customs Hall - Passenger Volume">
      <MultiSeriesChart
        series={data.map((d, i) => ({
          name: "Customs " + customNames[i],
          values: d.values,
        }))}
        labels={data[0].labels}
        xAxisTitle={"Time"}
        yAxisTitle={"Passengers"}
        totalRequired={true}
        chartType="column"
      />
    </Card>
  );
}

function MultiBaggagePassengers() {
  const { reportId } = useReportContext();
  const { airport } = useSessionData();
  let baggageNames = [];
  const { data, isSuccess } = getAggregateData(
    airport.baggage_halls.map((t) => {
      baggageNames.push(t.name);
      return {
        query: {
          measure: "pax",
          baggage_id: t.id,
          type: "baggage",
          flight_type: "international",
        },
      };
    }),
    reportId
  );

  if (!isSuccess || data.length === 0) {
    return null;
  }
  return (
    <Card title="Intl Bag Claim Hall - Passenger Volume">
      <MultiSeriesChart
        series={data
          .filter((d) => d.sum > 0)
          .map((d, i) => ({
            name: "Baggage " + baggageNames[i],
            values: d.values,
          }))}
        labels={data[0].labels}
        xAxisTitle={"Time"}
        yAxisTitle={"Passengers"}
        totalRequired={true}
        chartType="column"
      />
    </Card>
  );
}

function BaggageCarouselMetrics() {
  const { data: carousels, isLoading } = useBaggageCarousels();

  if (isLoading) {
    return null;
  }

  if (!carousels || carousels.length === 0) {
    return (
      <div className="col-span-full py-2">
        <Card title="Baggage Carousel - Configuration Required">
          <div className="flex items-center justify-center p-12 text-center">
            <div>
              <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">No Carousels Setup</p>
              <p className="text-sm text-muted-foreground mb-4">
                Please configure baggage carousels to view this data
              </p>
              <Link to="/setup/airport?tab=baggage">
                <Button variant="secondary">Configure Carousels →</Button>
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="col-span-full">
        <Card title="Baggage Carousel - Passengers (International)">
          <ExploreDisplay
            height={800}
            query={{
              type: "baggage_carousel",
              measure: "pax",
              direction: "arr",
              flight_type: "international",
            }}
          />
        </Card>
      </div>
      <div className="col-span-full">
        <Card title="Baggage Carousel - Bags (International)">
          <ExploreDisplay
            height={800}
            query={{
              type: "baggage_carousel",
              measure: "bag",
              direction: "arr",
              flight_type: "international",
            }}
            customTooltipFormatter={bagBreakdownTooltipFormatter}
            customCellFormatter={bagBreakdownCellFormatter}
          />
        </Card>
      </div>
    </>
  );
}

export function CustomsPage() {
  const { setTitles } = usePage();
  const { airport } = useSessionData();
  const multiCustoms = airport.customs.length > 1;
  const multiBaggage = airport.baggage_halls.length > 1;
  const { reportId } = useReportContext();
  const { data: plays, isLoading: playsLoading } = getPlays(reportId, {
    locations: ["customs", "intl_baggage"],
  });

  useEffect(() => {
    setTitles(["Customs Overview"]);
  }, []);

  return (
    <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
      <div className="col-span-full">
        <Card title="Customs - Passenger Density">
          <ExploreDisplay
            query={{
              type: "ingress_customs",
              measure: "pax",
            }}
          />
        </Card>
      </div>
      <div className="h-auto md:h-90 col-span-full py-4">
        {multiCustoms ? (
          <MultiCustomsPassengers />
        ) : (
          <TrafficChart
            query={{
              type: "ingress_customs",
              measure: "pax",
            }}
            title={"Customs Hall - Passenger Volume"}
          />
        )}
      </div>
      <div className="h-auto md:h-90 col-span-full py-2">
        {multiBaggage ? (
          <MultiBaggagePassengers />
        ) : (
          <TrafficChart
            query={{
              type: "baggage",
              measure: "pax",
              flight_type: "international",
            }}
            title={"Intl Bag Claim Hall - Passenger Volume"}
          />
        )}
      </div>
      <BaggageCarouselMetrics />
      <div className="col-span-full py-2">
        <Card
          title="Customs and Intl Baggage Hall Pain Points"
          innerClassName=" w-full"
        >
          {playsLoading && <Skeleton className="w-full h-full" />}
          {plays && <PlayListDisplay />}
        </Card>
      </div>
    </div>
  );
}
